import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Check, ImageIcon, Loader2, X } from 'lucide-react';
import { slideshowLibraryApi } from '@/lib/api';
import { sortByNatural } from '@/lib/utils';
import { Button } from '@/components/ui/button';

/**
 * Pick images from the facility's library to add to a slideshow. Images
 * already in the slideshow are marked but stay selectable (the same image
 * may appear twice, e.g. a title sponsor). Order added = order clicked.
 */
export function LibraryPickerDialog({
  facilityId,
  inSlideshow,
  adding,
  onAdd,
  onClose,
}: {
  facilityId?: string | undefined;
  /** Library image ids already used by this slideshow. */
  inSlideshow: Set<number>;
  adding: boolean;
  onAdd: (libraryImageIds: number[]) => void;
  onClose: () => void;
}) {
  const [selected, setSelected] = useState<number[]>([]);
  const { data, isLoading } = useQuery({
    queryKey: ['slideshow-library', facilityId],
    queryFn: () => slideshowLibraryApi.list(facilityId),
  });
  const images = sortByNatural(data?.images ?? [], i => i.originalName);

  const toggle = (id: number) =>
    setSelected(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div
        className="flex max-h-[85vh] w-full max-w-3xl flex-col rounded-xl border border-gray-200 bg-white shadow-xl dark:border-gray-700 dark:bg-gray-900"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4 dark:border-gray-700">
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-gray-100">Add from Image Library</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">Click images in the order you want them added.</p>
          </div>
          <button onClick={onClose} className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-800 dark:hover:text-gray-200">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {isLoading ? (
            <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>
          ) : images.length === 0 ? (
            <div className="py-12 text-center">
              <ImageIcon className="mx-auto mb-3 h-10 w-10 text-gray-300 dark:text-gray-600" />
              <p className="text-sm text-gray-500 dark:text-gray-400">The library is empty — upload images first.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              {images.map(img => {
                const order = selected.indexOf(img.id);
                const isSelected = order >= 0;
                return (
                  <button
                    key={img.id}
                    onClick={() => toggle(img.id)}
                    className={`relative rounded-lg border-2 bg-white p-1.5 text-left transition-colors dark:bg-gray-800 ${
                      isSelected
                        ? 'border-blue-500'
                        : 'border-gray-200 hover:border-gray-400 dark:border-gray-700 dark:hover:border-gray-500'
                    }`}
                  >
                    <div className="aspect-[43/10] overflow-hidden rounded bg-gray-100 dark:bg-gray-700">
                      <img src={img.signedUrl} alt={img.originalName} className="h-full w-full object-contain" />
                    </div>
                    <p className="mt-1 truncate text-xs font-medium text-gray-800 dark:text-gray-200" title={img.originalName}>
                      {img.originalName}
                    </p>
                    {inSlideshow.has(img.id) && (
                      <p className="text-[11px] text-gray-500 dark:text-gray-400">Already in this slideshow</p>
                    )}
                    {isSelected && (
                      <span className="absolute right-2 top-2 flex h-6 w-6 items-center justify-center rounded-full bg-blue-600 text-xs font-semibold text-white">
                        {order + 1}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-gray-200 px-5 py-3 dark:border-gray-700">
          <Button variant="outline" size="sm" onClick={onClose} disabled={adding}>Cancel</Button>
          <Button size="sm" disabled={selected.length === 0 || adding} onClick={() => onAdd(selected)}>
            {adding
              ? <><Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />Adding…</>
              : <><Check className="mr-2 h-3.5 w-3.5" />Add {selected.length || ''} image{selected.length === 1 ? '' : 's'}</>}
          </Button>
        </div>
      </div>
    </div>
  );
}
